import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import {
  Check,
  ChevronDown,
  Clock3,
  Grid2X2,
  Minus,
  Monitor,
  Plus,
  RectangleHorizontal,
  Scan,
  Sparkles,
  Volume2,
  VolumeX,
} from "lucide-react";
import type { CapabilityInfo, ParamSchema } from "@/lib/api";
import { cn } from "@/lib/utils";
import { quickParamNames } from "@/components/studio/studioConfig";
import { AUTO_SIZE, DEFAULT_IMAGE_SIZE_RULES, composeFromTable, composeImageSize, describeFromTable, describeImageSize, imageSizeLabel, isExperimentalSize } from "@/lib/imageSize";

interface NativeOption {
  value: string;
  label: string;
}

const COUNT_NAMES = new Set(["max_images", "num_images", "output_count", "n"]);
const RATIO_NAMES = new Set(["ratio", "aspect_ratio"]);
const RESOLUTION_NAMES = new Set(["resolution", "size", "quality"]);

const IMAGE_SIZE_LABELS: Record<string, string> = {
  square_hd: "1:1 HD",
  square: "1:1",
  portrait_4_3: "3:4",
  portrait_16_9: "9:16",
  landscape_4_3: "4:3",
  landscape_16_9: "16:9",
  auto_2K: "Auto 2K",
  auto_4K: "Auto 4K",
};

function optionsFor(param: ParamSchema): NativeOption[] {
  return (param.options ?? []).map((option) =>
    typeof option === "string" ? { value: option, label: IMAGE_SIZE_LABELS[option] ?? option } : { value: option.value, label: option.label }
  );
}

function humanize(value: unknown): string {
  return String(value ?? "Auto")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function durationLabel(value: unknown): string {
  if (value === -1 || value === "-1") return "Auto";
  const raw = String(value ?? "");
  return /s$/i.test(raw) ? raw : `${raw}s`;
}

function resolutionDescription(value: string): string | undefined {
  const descriptions: Record<string, string> = {
    "1K": "Fast · Quick generation, good resolution",
    "2K": "Balanced · Recommended for most use cases",
    "3K": "Detailed · Higher clarity and processing time",
    "4K": "Ultra · Highest detail, longer processing",
    "480p": "Fast · Lightweight video output",
    "720p": "Balanced · Standard HD output",
    "1080p": "Full HD · Higher detail",
    "4k": "Ultra · Highest video detail",
    auto: "Automatic · Model picks — measured low to medium",
    low: "Fast · Lower processing cost",
    medium: "Balanced · Recommended default",
    high: "High · More detail and processing",
    xhigh: "Extra high · Finer detail, longer processing",
    max: "Max · Highest quality, more processing",
  };
  return descriptions[value] ?? descriptions[value.toLowerCase()];
}

function ratioFromOption(value: string): string | null {
  if (/^\d+:\d+$/.test(value)) return value;
  const label = IMAGE_SIZE_LABELS[value];
  const match = label?.match(/^\d+:\d+/);
  return match?.[0] ?? null;
}

function RatioGlyph({ ratio, active = false }: { ratio: string; active?: boolean }) {
  const [width, height] = ratio.split(":").map(Number);
  const box = 17;
  let glyphWidth = box;
  let glyphHeight = box;
  if (width >= height) glyphHeight = Math.max(4, Math.round((box * height) / width));
  else glyphWidth = Math.max(4, Math.round((box * width) / height));
  return (
    <span className="flex h-5 w-5 shrink-0 items-center justify-center">
      <span
        className={cn("rounded-[2px] border", active ? "border-studio-accent bg-studio-accent/15" : "border-studio-muted")}
        style={{ width: glyphWidth, height: glyphHeight }}
      />
    </span>
  );
}

function ControlPopover({
  label,
  value,
  icon,
  children,
  className,
  compact = false,
  disabled = false,
  title,
}: {
  label: string;
  value: string;
  icon: ReactNode;
  children: (close: () => void) => ReactNode;
  className?: string;
  compact?: boolean;
  disabled?: boolean;
  title?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelPosition, setPanelPosition] = useState<{ left: number; width: number; top?: number; bottom?: number; maxHeight: number } | null>(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return;
    const handlePointer = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!ref.current?.contains(target) && !panelRef.current?.contains(target)) setOpen(false);
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    const handleViewportChange = () => setOpen(false);
    const triggerPosition = buttonRef.current?.getBoundingClientRect();
    const handleScroll = () => {
      const current = buttonRef.current?.getBoundingClientRect();
      if (!current || !triggerPosition
        || Math.abs(current.top - triggerPosition.top) > 1
        || Math.abs(current.left - triggerPosition.left) > 1) setOpen(false);
    };
    document.addEventListener("pointerdown", handlePointer);
    document.addEventListener("keydown", handleKey);
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("pointerdown", handlePointer);
      document.removeEventListener("keydown", handleKey);
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [open]);

  const toggle = () => {
    if (disabled) return;
    if (!open && buttonRef.current) {
      const trigger = buttonRef.current.getBoundingClientRect();
      const viewportPadding = 12;
      const viewport = window.visualViewport;
      const viewportLeft = Math.max(0, viewport?.offsetLeft ?? 0);
      const viewportRight = Math.min(
        document.documentElement.clientWidth,
        viewportLeft + (viewport?.width ?? document.documentElement.clientWidth)
      );
      const viewportWidth = viewportRight - viewportLeft;
      const width = Math.min(compact ? 224 : 288, viewportWidth - viewportPadding * 2);
      const centeredLeft = compact ? trigger.left : trigger.left + trigger.width / 2 - width / 2;
      const minLeft = viewportLeft + viewportPadding;
      const maxLeft = viewportLeft + viewportWidth - width - viewportPadding;
      const left = Math.max(minLeft, Math.min(centeredLeft, maxLeft));
      const viewportTop = Math.max(0, viewport?.offsetTop ?? 0);
      const viewportBottom = Math.min(window.innerHeight, viewportTop + (viewport?.height ?? window.innerHeight));
      // The bar sits at the bottom of the composer, so panels open upward; they
      // flip below only when the trigger is nearer the top and below has more room.
      const gap = 8;
      const spaceAbove = trigger.top - viewportTop - gap - viewportPadding;
      const spaceBelow = viewportBottom - trigger.bottom - gap - viewportPadding;
      setPanelPosition({
        left,
        width,
        ...(spaceAbove >= spaceBelow
          ? { bottom: window.innerHeight - trigger.top + gap, maxHeight: Math.max(40, spaceAbove) }
          : { top: trigger.bottom + gap, maxHeight: Math.max(40, spaceBelow) }),
      });
    }
    setOpen((current) => !current);
  };

  return (
    <div ref={ref} className={cn("relative", className)}>
      <button
        ref={buttonRef}
        type="button"
        aria-label={`${label}: ${value}`}
        aria-expanded={open}
        aria-controls={panelId}
        aria-haspopup="listbox"
        aria-disabled={disabled || undefined}
        disabled={disabled}
        title={title}
        onClick={toggle}
        className={cn(
          "inline-flex min-h-9 items-center gap-1.5 rounded-lg border px-2.5 text-[10px] font-semibold transition-colors focus-visible:outline-none focus-visible:shadow-glow-sm disabled:cursor-not-allowed disabled:opacity-50",
          open
            ? "border-studio-accent/45 bg-studio-accent-soft text-studio-accent"
            : "border-studio-border bg-studio-card text-studio-ink hover:border-studio-border-light"
        )}
      >
        <span className={open ? "text-studio-accent" : "text-studio-muted"}>{icon}</span>
        <span>{value}</span>
        <ChevronDown className={cn("h-3 w-3 text-studio-muted transition-transform", open && "rotate-180")} />
      </button>
      {open && panelPosition && createPortal(
        <div
          ref={panelRef}
          id={panelId}
          style={panelPosition}
          className="fixed z-50 overflow-y-auto overscroll-contain rounded-xl border border-studio-border-light bg-studio-panel p-2 shadow-panel animate-rise"
        >
          <p className="px-2 pb-1.5 pt-1 text-[10px] font-semibold text-studio-muted">{label}</p>
          {children(() => setOpen(false))}
        </div>,
        document.body
      )}
    </div>
  );
}

function OptionControl({ param, value, onChange }: { param: ParamSchema; value: unknown; onChange: (value: unknown) => void }) {
  const options = optionsFor(param);
  const ratioControl = RATIO_NAMES.has(param.name) || (param.name === "image_size" && options.some((option) => ratioFromOption(option.value)));
  const resolutionControl = RESOLUTION_NAMES.has(param.name) || (param.name === "image_size" && !ratioControl);
  const selected = options.find((option) => String(option.value) === String(value)) ?? options[0];
  // Quality sits next to the size composer's own "Resolution" trigger; keep the two apart.
  const label = ratioControl ? "Aspect ratio" : param.name === "quality" ? "Quality" : resolutionControl ? "Resolution" : param.label ?? humanize(param.name);
  const icon = ratioControl ? <RectangleHorizontal className="h-3.5 w-3.5" /> : resolutionControl ? <Monitor className="h-3.5 w-3.5" /> : <Sparkles className="h-3.5 w-3.5" />;

  return (
    <ControlPopover label={label} value={selected?.label ?? humanize(value)} icon={icon} compact={ratioControl}>
      {(close) => (
        <div role="listbox" aria-label={label} className={ratioControl ? "grid grid-cols-2 gap-1" : "space-y-0.5"}>
          {options.map((option) => {
            const active = String(option.value) === String(value);
            const ratio = ratioFromOption(option.value);
            const description = resolutionControl ? resolutionDescription(option.value) : undefined;
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => { onChange(option.value); close(); }}
                className={cn(
                  "flex min-h-10 w-full items-center rounded-lg text-left transition-colors",
                  ratioControl ? "gap-1.5 px-1.5 py-1" : "gap-2.5 px-2.5 py-2",
                  active ? "bg-studio-card-hover text-studio-ink" : "text-studio-muted hover:bg-studio-card hover:text-studio-ink"
                )}
              >
                {ratio ? <RatioGlyph ratio={ratio} active={active} /> : <span className="flex h-5 w-5 shrink-0 items-center justify-center"><Monitor className="h-3.5 w-3.5" /></span>}
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2 text-[11px] font-semibold">
                    {option.label}
                  </span>
                  {description && <span className="mt-0.5 block text-[9px] leading-4 text-studio-muted">{description}</span>}
                </span>
                {active && <Check className="h-3.5 w-3.5 shrink-0 text-studio-accent" />}
              </button>
            );
          })}
        </div>
      )}
    </ControlPopover>
  );
}

const TIER_DESCRIPTIONS: Record<string, string> = {
  "1K": "Fast · about 1 MP",
  "2K": "Recommended · up to 3.7 MP, sharpest texture",
  "4K": "Ultra · up to 8.3 MP, experimental: use xhigh or max",
};
// Models without a documented experimental line (GPT Image 2) get plain sizes.
const PLAIN_TIER_DESCRIPTIONS: Record<string, string> = {
  "1K": "Fast · about 1 MP",
  "2K": "About 3.7 MP",
  "4K": "Ultra · up to 8.3 MP",
};

/**
 * GPT Image size: an aspect ratio and a 1K/2K/4K tier compose one
 * WIDTHxHEIGHT the provider accepts (see lib/imageSize). "Auto" hands the
 * choice to the model, which is why the tier is disabled while it is active.
 */
export function ImageSizeControls({
  param,
  value,
  onChange,
  layout = "quick",
}: {
  param: ParamSchema;
  value: unknown;
  onChange: (value: unknown) => void;
  layout?: "quick" | "form";
}) {
  const ratios = param.aspect_ratios ?? [];
  const tiers = param.resolutions ?? [];
  const rules = param.size_rules ?? DEFAULT_IMAGE_SIZE_RULES;
  // Seedream publishes exact sizes per level and ratio; its "auto" still sends
  // a level, so the level stays selectable there.
  const table = param.size_table;
  const compose = (ratio: string, resolution: string) =>
    table ? composeFromTable(table, ratio, resolution) : composeImageSize(ratio, resolution, rules);
  const current = table ? describeFromTable(table, value, ratios, tiers) : describeImageSize(value, ratios, tiers);
  const [tier, setTier] = useState(current.resolution ?? tiers[0] ?? "1K");
  useEffect(() => {
    if (current.resolution) setTier(current.resolution);
  }, [current.resolution]);
  const isAuto = current.ratio === AUTO_SIZE;
  const tierLocked = isAuto && !table;
  // A size saved before 2026-10-08 (e.g. "last used" 1536x2736) still reads as
  // 2K but crosses the experimental line the 2K tier now stays under; move it to
  // the current 2K size for the same ratio so the label and the request agree.
  const migrated = !table && !isAuto && current.resolution ? composeImageSize(current.ratio, current.resolution, rules) : null;
  const stale = migrated !== null && migrated !== value && current.resolution === "2K" && isExperimentalSize(value, rules);
  useEffect(() => {
    if (stale && migrated) onChange(migrated);
  }, [stale, migrated, onChange]);
  const pickRatio = (ratio: string) => onChange(compose(ratio, tier));
  const pickTier = (next: string) => {
    setTier(next);
    if (!tierLocked) onChange(compose(current.ratio, next));
  };
  const ratioLabel = isAuto ? "Auto" : current.ratio;
  const tierLabel = tierLocked ? "Auto" : tier;
  const tierHint = tierLocked ? "Choose an aspect ratio to pick a resolution" : undefined;

  const ratioOptions = ratios.map((ratio) => {
    const active = ratio === current.ratio;
    return { ratio, active, glyph: ratio === AUTO_SIZE ? <span className="flex h-5 w-5 shrink-0 items-center justify-center"><Scan className="h-3.5 w-3.5" /></span> : <RatioGlyph ratio={ratio} active={active} /> };
  });
  const tierOptions = tiers.map((name) => ({
    name,
    active: !tierLocked && name === tier,
    pixels: tierLocked ? null : table && isAuto ? "ratio from prompt" : imageSizeLabel(compose(current.ratio, name)),
    description: table ? undefined : (rules.experimental_above ? TIER_DESCRIPTIONS : PLAIN_TIER_DESCRIPTIONS)[name],
  }));

  if (layout === "form") {
    return (
      <div className="space-y-3" data-testid="image-size-controls">
        <div role="listbox" aria-label="Aspect ratio" className="grid grid-cols-3 gap-1.5 sm:grid-cols-5">
          {ratioOptions.map(({ ratio, active, glyph }) => (
            <button key={ratio} type="button" role="option" aria-selected={active} onClick={() => pickRatio(ratio)}
              className={cn("flex min-h-11 flex-col items-center gap-1 rounded-lg border px-1 py-2 transition-[background-color,border-color,color]",
                active ? "border-studio-accent/50 bg-studio-accent/10 text-studio-ink" : "border-studio-border bg-studio-card text-studio-muted hover:border-studio-border-light hover:text-studio-ink")}>
              {glyph}
              <span className="font-mono text-[10px]">{ratio === AUTO_SIZE ? "Auto" : ratio}</span>
            </button>
          ))}
        </div>
        <div role="listbox" aria-label="Resolution" className="grid grid-cols-3 gap-1.5">
          {tierOptions.map(({ name, active, pixels }) => (
            <button key={name} type="button" role="option" aria-selected={active} disabled={tierLocked} onClick={() => pickTier(name)} title={tierHint}
              className={cn("flex min-h-11 flex-col items-center justify-center rounded-lg border px-1 py-1.5 text-[11px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                active ? "border-studio-accent/50 bg-studio-accent/10 text-studio-ink" : "border-studio-border bg-studio-card text-studio-muted hover:border-studio-border-light hover:text-studio-ink")}>
              {name}
              <span className="font-mono text-[9px] font-normal text-studio-muted">{pixels ?? "—"}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      <ControlPopover label="Aspect ratio" value={ratioLabel} icon={<RectangleHorizontal className="h-3.5 w-3.5" />} compact>
        {(close) => (
          <div role="listbox" aria-label="Aspect ratio" className="grid grid-cols-2 gap-1">
            {ratioOptions.map(({ ratio, active, glyph }) => (
              <button key={ratio} type="button" role="option" aria-selected={active}
                onClick={() => { pickRatio(ratio); close(); }}
                className={cn("flex min-h-10 w-full items-center gap-1.5 rounded-lg px-1.5 py-1 text-left transition-colors",
                  active ? "bg-studio-card-hover text-studio-ink" : "text-studio-muted hover:bg-studio-card hover:text-studio-ink")}>
                {glyph}
                <span className="min-w-0 flex-1 text-[11px] font-semibold">{ratio === AUTO_SIZE ? "Auto" : ratio}</span>
                {active && <Check className="h-3.5 w-3.5 shrink-0 text-studio-accent" />}
              </button>
            ))}
          </div>
        )}
      </ControlPopover>
      <ControlPopover label="Resolution" value={tierLabel} icon={<Monitor className="h-3.5 w-3.5" />} disabled={tierLocked} title={tierHint}>
        {(close) => (
          <div role="listbox" aria-label="Resolution" className="space-y-0.5">
            {tierOptions.map(({ name, active, pixels, description }) => (
              <button key={name} type="button" role="option" aria-selected={active}
                onClick={() => { pickTier(name); close(); }}
                className={cn("flex min-h-10 w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors",
                  active ? "bg-studio-card-hover text-studio-ink" : "text-studio-muted hover:bg-studio-card hover:text-studio-ink")}>
                <span className="flex h-5 w-5 shrink-0 items-center justify-center"><Monitor className="h-3.5 w-3.5" /></span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2 text-[11px] font-semibold">{name}{pixels && <span className="font-mono text-[10px] font-normal text-studio-muted">{pixels}</span>}</span>
                  {description && <span className="mt-0.5 block text-[9px] leading-4 text-studio-muted">{description}</span>}
                </span>
                {active && <Check className="h-3.5 w-3.5 shrink-0 text-studio-accent" />}
              </button>
            ))}
          </div>
        )}
      </ControlPopover>
    </>
  );
}

function DurationControl({ param, value, onChange }: { param: ParamSchema; value: unknown; onChange: (value: unknown) => void }) {
  const options = optionsFor(param);
  const isDiscrete = options.length > 0;
  const optionIndex = Math.max(0, options.findIndex((option) => String(option.value) === String(value)));
  const sliderMin = Math.max(1, param.min ?? 1);
  const sliderMax = Math.max(sliderMin, param.max ?? 15);
  const numericValue = value === -1 || value === "-1" ? sliderMin : Number(value || param.default || sliderMin);

  return (
    <ControlPopover label="Duration" value={durationLabel(value)} icon={<Clock3 className="h-3.5 w-3.5" />}>
      {() => (
        <div className="px-2 pb-2 pt-1">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-[10px] text-studio-muted">Duration</span>
            <span className="rounded-md bg-studio-card px-2 py-1 text-xs font-semibold text-studio-ink">{durationLabel(value)}</span>
          </div>
          <input
            type="range"
            aria-label="Duration"
            min={isDiscrete ? 0 : sliderMin}
            max={isDiscrete ? Math.max(0, options.length - 1) : sliderMax}
            step={1}
            value={isDiscrete ? optionIndex : numericValue}
            onChange={(event) => {
              const next = Number(event.target.value);
              onChange(isDiscrete ? options[next].value : next);
            }}
            className="h-1.5 w-full cursor-pointer accent-studio-accent"
          />
          <div className="mt-2 flex items-center justify-between text-[9px] text-studio-muted">
            <span>{isDiscrete ? durationLabel(options[0]?.value) : `${sliderMin}s`}</span>
            <span>{isDiscrete ? durationLabel(options.at(-1)?.value) : `${sliderMax}s`}</span>
          </div>
          {(param.min ?? 0) < 0 && (
            <button
              type="button"
              onClick={() => onChange(-1)}
              className={cn(
                "mt-3 w-full rounded-lg border px-3 py-2 text-[10px] font-semibold",
                value === -1 || value === "-1"
                  ? "border-studio-accent/40 bg-studio-accent-soft text-studio-accent"
                  : "border-studio-border text-studio-muted hover:text-studio-ink"
              )}
            >
              Auto duration
            </button>
          )}
        </div>
      )}
    </ControlPopover>
  );
}

function CountControls({ param, value, onChange }: { param: ParamSchema; value: unknown; onChange: (value: unknown) => void }) {
  const min = param.min ?? 1;
  const max = param.max ?? 4;
  const numeric = Math.min(max, Math.max(min, Number(value || param.default || min)));
  const grids = [
    { value: 1, label: "1x1" },
    { value: 4, label: "2x2" },
    { value: 9, label: "3x3" },
    { value: 16, label: "4x4" },
  ];
  const currentGrid = grids.find((grid) => grid.value === numeric)?.label ?? "Grid";

  return (
    <>
      <ControlPopover label="Grid generation" value={currentGrid} icon={<Grid2X2 className="h-3.5 w-3.5" />}>
        {(close) => (
          <div role="listbox" aria-label="Grid generation" className="space-y-0.5">
            {grids.map((grid) => {
              const disabled = grid.value > max;
              const active = grid.value === numeric;
              return (
                <button
                  key={grid.label}
                  type="button"
                  role="option"
                  aria-selected={active}
                  disabled={disabled}
                  onClick={() => { onChange(grid.value); close(); }}
                  className={cn(
                    "flex min-h-10 w-full items-center justify-between rounded-lg px-3 text-[11px] font-semibold transition-colors",
                    active ? "bg-studio-card-hover text-studio-ink" : "text-studio-muted hover:bg-studio-card hover:text-studio-ink",
                    disabled && "cursor-not-allowed opacity-35"
                  )}
                >
                  <span className="flex items-center gap-2"><Grid2X2 className="h-3.5 w-3.5" /> {grid.label}</span>
                  {active && <Check className="h-3.5 w-3.5 text-studio-accent" />}
                </button>
              );
            })}
            {max < 16 && <p className="px-3 pb-1 pt-2 text-[9px] leading-4 text-studio-muted">This model supports up to {max} outputs per generation.</p>}
          </div>
        )}
      </ControlPopover>
      <div className="inline-flex min-h-9 items-center rounded-lg border border-studio-border bg-studio-card" aria-label={`Output count: ${numeric} of ${max}`}>
        <button
          type="button"
          aria-label="Decrease output count"
          disabled={numeric <= min}
          onClick={() => onChange(Math.max(min, numeric - (param.step ?? 1)))}
          className="flex h-9 w-8 items-center justify-center rounded-l-lg text-studio-muted hover:bg-studio-card-hover hover:text-studio-ink disabled:opacity-30"
        >
          <Minus className="h-3 w-3" />
        </button>
        <span className="min-w-11 text-center text-[10px] font-semibold text-studio-ink">{numeric} / {max}</span>
        <button
          type="button"
          aria-label="Increase output count"
          disabled={numeric >= max}
          onClick={() => onChange(Math.min(max, numeric + (param.step ?? 1)))}
          className="flex h-9 w-8 items-center justify-center rounded-r-lg text-studio-muted hover:bg-studio-card-hover hover:text-studio-ink disabled:opacity-30"
        >
          <Plus className="h-3 w-3" />
        </button>
      </div>
    </>
  );
}

export function StudioQuickControls({
  capability,
  params,
  onChange,
}: {
  capability: CapabilityInfo | null;
  params: Record<string, unknown>;
  onChange: (name: string, value: unknown) => void;
}) {
  if (!capability) return null;
  const quickNames = new Set(quickParamNames(capability));
  const quickParams = capability.ui_schema.params.filter((param) => quickNames.has(param.name));
  if (!quickParams.length) return null;

  return (
    <div
      className="flex flex-wrap items-center gap-1.5 rounded-lg border border-studio-border bg-studio-panel/60 p-1"
      aria-label="Output settings"
    >
      {quickParams.map((param) => {
        const value = params[param.name] ?? param.default ?? "";
        if (COUNT_NAMES.has(param.name)) {
          return <CountControls key={param.name} param={param} value={value} onChange={(next) => onChange(param.name, next)} />;
        }
        if (param.name === "duration") {
          return <DurationControl key={param.name} param={param} value={value} onChange={(next) => onChange(param.name, next)} />;
        }
        if (param.widget === "toggle") {
          const active = Boolean(value);
          return (
            <button
              key={param.name}
              type="button"
              aria-label={`${param.label ?? humanize(param.name)}: ${active ? "On" : "Off"}`}
              aria-pressed={active}
              onClick={() => onChange(param.name, !active)}
              className={cn(
                "inline-flex min-h-9 items-center gap-1.5 rounded-lg border px-2.5 text-[10px] font-semibold transition-colors",
                active
                  ? "border-studio-accent/35 bg-studio-accent-soft text-studio-accent"
                  : "border-studio-border bg-studio-card text-studio-muted hover:text-studio-ink"
              )}
            >
              {active ? <Volume2 className="h-3.5 w-3.5" /> : <VolumeX className="h-3.5 w-3.5" />}
              {active ? "Audio on" : "Audio off"}
            </button>
          );
        }
        if (param.name === "size" && param.aspect_ratios?.length) {
          return <ImageSizeControls key={param.name} param={param} value={value} onChange={(next) => onChange(param.name, next)} />;
        }
        if (optionsFor(param).length > 0) {
          return <OptionControl key={param.name} param={param} value={value} onChange={(next) => onChange(param.name, next)} />;
        }
        return null;
      })}
    </div>
  );
}
